import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, CircularProgress, Card, CardContent,
  CardMedia, Grid, Stack, MenuItem, Divider, Avatar, List, ListItem,
  ListItemText, DialogActions, Chip, ToggleButton, ToggleButtonGroup, Paper
} from "@mui/material";

import {
  AddPhotoAlternate as AddPhotoAlternateIcon,
  Delete as DeleteIcon, Edit as EditIcon,
  CloudUpload as CloudUploadIcon,
  Collections as AllPhotosIcon,
  Timeline as TimelineIcon,
  ChatBubbleOutline as ChatIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Close as CloseIcon
} from "@mui/icons-material";

import Swal from "sweetalert2";
import { db, auth, storage } from "../../../firebase";
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs,
  query, orderBy, serverTimestamp, arrayUnion, arrayRemove,
  getDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const PRIMARY_INDIGO = "#6366F1";

function Dailyposts() {
  const [posts, setPosts] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [viewMode, setViewMode] = useState("timeline");
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);

  // Dialog Controls
  const [open, setOpen] = useState(false); // Form Dialog
  const [detailOpen, setDetailOpen] = useState(false); // Image Detail Dialog
  const [activePost, setActivePost] = useState(null);
  const [commentText, setCommentText] = useState("");

  const [form, setForm] = useState({
    title: "", content: "", files: [], postType: "", subCategory: "",
    date: new Date().toISOString().split("T")[0]
  });
  const [previewFiles, setPreviewFiles] = useState([]);
  const [editId, setEditId] = useState(null);

  // Inside Dailyposts function
  const selectedCategoryObj = categoriesData.find(c => c.title === form.postType);
  const availableSubCategories = selectedCategoryObj?.subcategories || [];

  useEffect(() => {
    fetchInitialData();
    fetchPosts();
  }, []);

  const fetchInitialData = async () => {
    try {
      const typeSnap = await getDocs(query(collection(db, "post_types"), orderBy("createdAt", "asc")));
      setCategoriesData(typeSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "dailyposts"), orderBy("date", "desc"));
      const snap = await getDocs(q);
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleLike = async (post) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    const isLiked = post.likes?.includes(userId);
    const postRef = doc(db, "dailyposts", post.id);

    await updateDoc(postRef, {
      likes: isLiked ? arrayRemove(userId) : arrayUnion(userId)
    });

    // Update local state for immediate feedback in the popup
    if (activePost && activePost.id === post.id) {
      setActivePost({
        ...post,
        likes: isLiked ? post.likes.filter(id => id !== userId) : [...(post.likes || []), userId]
      });
    }
    fetchPosts();
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !activePost) return;
    const newComment = {
      text: commentText,
      userName: auth.currentUser?.displayName || "User",
      userId: auth.currentUser?.uid,
      createdAt: new Date().toISOString()
    };
    await updateDoc(doc(db, "dailyposts", activePost.id), { comments: arrayUnion(newComment) });
    setCommentText("");
    setActivePost({ ...activePost, comments: [...(activePost.comments || []), newComment] });
    fetchPosts();
  };

  const handleSubmit = async () => {
    if (!form.title || !form.postType) return Swal.fire("Error", "Missing fields", "error");
    setLoading(true);
    try {
      let finalUrls = editId ? previewFiles : [];
      if (form.files.length > 0) {
        const uploads = [];
        for (const file of form.files) {
          const sRef = ref(storage, `posts/${Date.now()}_${file.name}`);
          await uploadBytes(sRef, file);
          uploads.push(await getDownloadURL(sRef));
        }
        finalUrls = uploads;
      }

      const data = {
        title: form.title, content: form.content, postType: form.postType,
        subCategory: form.subCategory, date: form.date, files: finalUrls,
        updatedAt: serverTimestamp(), userId: auth.currentUser?.uid
      };

      if (editId) { await updateDoc(doc(db, "dailyposts", editId), data); }
      else {
        data.createdAt = serverTimestamp(); data.likes = []; data.comments = [];
        await addDoc(collection(db, "dailyposts"), data);
      }
      setOpen(false); resetForm(); fetchPosts();
    } catch (err) { Swal.fire("Error", err.message, "error"); }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ title: "", content: "", files: [], postType: "", subCategory: "", date: selectedDate });
    setPreviewFiles([]); setEditId(null);
  };

  // Grouping for All-Photos view but keep reference to original post
  const groupedData = posts.reduce((acc, post) => {
    if (post.files) {
      if (!acc[post.date]) acc[post.date] = [];
      post.files.forEach(url => acc[post.date].push({ url, post }));
    }
    return acc;
  }, {});

  // Add this helper component ABOVE your Dailyposts function
  const CommentItem = ({ comment }) => {
    const [userName, setUserName] = useState("Loading...");

    useEffect(() => {
      const fetchUserName = async () => {
        if (!comment.userId) return;
        try {
          const userDoc = await getDoc(doc(db, "users", comment.userId));
          if (userDoc.exists()) {
            setUserName(userDoc.data().name || "Anonymous");
          } else {
            setUserName("Unknown User");
          }
        } catch (err) {
          setUserName("User");
        }
      };
      fetchUserName();
    }, [comment.userId]);

    // Helper to format date nicely
    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
      <ListItem alignItems="flex-start" sx={{ px: 2, py: 1, mb: 1, bgcolor: 'white', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: PRIMARY_INDIGO, fontSize: '0.875rem' }}>
          {userName.charAt(0).toUpperCase()}
        </Avatar>
        <ListItemText
          primary={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight="700" color="text.primary">
                {userName}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {formatDate(comment.createdAt)}
              </Typography>
            </Stack>
          }
          secondary={
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.4 }}>
              {comment.text}
            </Typography>
          }
        />
      </ListItem>
    );
  };

  return (
    <Box p={1} sx={{ backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
      {/* HEADER */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: "1px solid #E2E8F0" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="900">Activity</Typography>
          <Stack direction="row" spacing={1}>
            <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)} size="small">
              <ToggleButton value="timeline"><TimelineIcon /></ToggleButton>
              <ToggleButton value="all-gallery"><AllPhotosIcon /></ToggleButton>
            </ToggleButtonGroup>
            <TextField type="date" size="small" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
            <Button variant="contained" onClick={() => setOpen(true)} sx={{ bgcolor: PRIMARY_INDIGO }}>Post</Button>
          </Stack>
        </Stack>
      </Paper>

      {/* VIEW MODES */}
      {viewMode === "timeline" ? (
        <Grid className="row" spacing={2}>
          {posts.filter(p => p.date === selectedDate).map(post => (
            <Grid className="col-sm-4 mb-3" item xs={12} sm={6} md={4} key={post.id}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary">{post.postType}</Typography>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>{post.title}</Typography>
                  <Grid className="row" spacing={0.5}>
                    {post.files?.map((img, i) => (
                      <Grid className="col-sm-3 mb-1" item xs={6} key={i}>
                        <CardMedia
                          component="img" image={img}
                          onClick={() => { setActivePost(post); setDetailOpen(true); }}
                          sx={{ height: 80, borderRadius: 2, cursor: 'pointer', objectFit: 'cover' }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        Object.keys(groupedData).sort((a, b) => b.localeCompare(a)).map(date => (
          <Box key={date} mb={4}>
            <Typography variant="button" sx={{ opacity: 0.6, mb: 1, display: 'block' }}>{date}</Typography>
            <Grid className="row" spacing={1}>
              {groupedData[date].map((item, i) => (
                <Grid className="col-sm-1 mb-1" item xs={4} sm={2} key={i}>
                  <CardMedia
                    component="img" image={item.url}
                    onClick={() => { setActivePost(item.post); setDetailOpen(true); }}
                    sx={{ aspectRatio: '1/1', borderRadius: 1.5, cursor: 'pointer', objectFit: 'cover' }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}

      {/* --- IMAGE DETAIL POPUP (Likes & Comments) --- */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
        <IconButton onClick={() => setDetailOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1, bgcolor: 'white' }}>
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          <Grid className="row" sx={{ minHeight: '400px' }}>
            {/* Left Side: Image Slider/Display */}
            <Grid className="col-sm-7 mb-1" item xs={12} md={7} sx={{ bgcolor: '#000', display: 'flex', alignItems: 'center' }}>
              <CardMedia
                component="img"
                image={activePost?.files?.[0]}
                sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
              />
            </Grid>
            {/* Right Side: Interactions */}
            <Grid className="col-sm-5 mb-1" item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column', height: 'auto' }}>
              <Box p={2}>
                <Typography variant="h6" fontWeight="bold">{activePost?.title}</Typography>
                <Typography variant="body2" color="text.secondary">{activePost?.content}</Typography>
                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={2} alignItems="center">
                  <Button
                    startIcon={activePost?.likes?.includes(auth.currentUser?.uid) ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
                    onClick={() => handleLike(activePost)}
                  >
                    {activePost?.likes?.length || 0} Likes
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {activePost?.comments?.length || 0} Comments
                  </Typography>
                </Stack>
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  overflowY: 'auto',
                  p: 2,
                  bgcolor: '#F1F5F9', // Light Slate background
                  maxHeight: '400px',
                  borderTop: '1px solid #E2E8F0',
                  borderBottom: '1px solid #E2E8F0',
                  '&::-webkit-scrollbar': { width: '4px' },
                  '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' }
                }}
              >
                {activePost?.comments && activePost.comments.length > 0 ? (
                  <List sx={{ p: 0 }}>
                    {activePost.comments.map((c, i) => (
                      <CommentItem key={i} comment={c} />
                    ))}
                  </List>
                ) : (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', opacity: 0.5 }}>
                    <ChatIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="body2">No comments yet. Start the conversation!</Typography>
                  </Stack>
                )}
              </Box>

              <Box p={2} component={Paper} elevation={3}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth size="small" placeholder="Add a comment..."
                    value={commentText} onChange={(e) => setCommentText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  />
                  <Button onClick={handleAddComment} variant="contained" sx={{ bgcolor: PRIMARY_INDIGO }}>Post</Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* NEW POST FORM (Keep your existing code) */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Activity</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} pt={1}>
            <TextField fullWidth label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            {/* <TextField select fullWidth label="Category" value={form.postType} onChange={(e) => setForm({ ...form, postType: e.target.value, subCategory: "" })}>
              {categoriesData.map(c => <MenuItem key={c.id} value={c.title}>{c.title}</MenuItem>)}
            </TextField> */}
            {/* Category Selection */}
            <TextField
              select
              fullWidth
              label="Category"
              value={form.postType}
              onChange={(e) => setForm({ ...form, postType: e.target.value, subCategory: "" })}
            >
              {categoriesData.map(c => (
                <MenuItem key={c.id} value={c.title}>{c.title}</MenuItem>
              ))}
            </TextField>

            {/* Subcategory Selection (Only shows if Category is selected) */}
            <TextField
              select
              fullWidth
              label="Sub Category"
              value={form.subCategory}
              disabled={!form.postType || availableSubCategories.length === 0}
              onChange={(e) => setForm({ ...form, subCategory: e.target.value })}
              helperText={!form.postType ? "Select a category first" : ""}
            >
              {availableSubCategories.map((sub, index) => (
                <MenuItem key={index} value={sub}>{sub}</MenuItem>
              ))}
            </TextField>
            <TextField type="date" fullWidth value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <TextField fullWidth multiline rows={3} label="Content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
            <Button variant="outlined" component="label">Upload Images<input type="file" hidden multiple onChange={(e) => {
              const files = Array.from(e.target.files);
              setForm({ ...form, files });
              setPreviewFiles(files.map(f => URL.createObjectURL(f)));
            }} /></Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: PRIMARY_INDIGO }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Dailyposts;